import { Button, Flex, Heading, Stack, Text } from "@caspeco/casper-ui-library";
import { useState } from "react";
import {
	ThemeColorVariable,
	ThemeFontSizeVariable,
	ThemeRadiusVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "../types/query-key-enum";
import { getAllSystems, getAllUserForSystem } from "../services/api/user-api";
import { System, systemMode } from "../models/system";
import SearchInput from "./search-input";
import { IUser } from "../models/user";
import { useUserContext } from "../hooks/use-user-context";

const ImpersonateSelect = () => {
	const [selectedSystem, setSelectedSystem] = useState<System>();
	const [selectedUser, setselectedUser] = useState<IUser>();
	const [availableUsers, setAvailableUsers] = useState<IUser[]>();
	const [userLoading, setUserLoading] = useState<boolean>();
	const { t } = useTranslation();
	const { user, isImpersonating, impersonateUser, stopImpersonatingUser } = useUserContext();

	const { data: systems, isLoading: systemLoading } = useQuery({
		queryKey: [QueryKeys.AllSystems],
		queryFn: async () => {
			const allSystems = (await getAllSystems())
				.filter((system) => system.haveMode([systemMode.Schedule, systemMode.Salary, systemMode.Time]))
				.sort((system1, system2) => system1.commonName.localeCompare(system2.commonName));

			return allSystems;
		},
		enabled: !isImpersonating,
	});

	const getUsers = (system: string) => {
		setUserLoading(true);
		getAllUserForSystem(system)
			.then((users) => {
				setAvailableUsers(users.sort((u1, u2) => u1.name.localeCompare(u2.name)));
			})
			.catch(() => null)
			.finally(() => {
				setUserLoading(false);
			});
	};

	return (
		<Stack
			alignItems={"center"}
			padding={ThemeSpaceVariable.Medium}
			backgroundColor={ThemeColorVariable.AccentContainer}
			color={ThemeColorVariable.OnAccentContainer}
			borderRadius={ThemeRadiusVariable.Medium}
			gap={ThemeSpaceVariable.Medium}
		>
			{isImpersonating ? (
				<>
					<Text>
						{t("impersonate")}: <b>{user?.name}</b>
					</Text>
					<Button w={"100%"} variant={"primary"} onClick={stopImpersonatingUser}>
						{t("impersonation_abort")}
					</Button>
				</>
			) : (
				<>
					<SearchInput
						items={systems ?? []}
						selectedItem={selectedSystem}
						onSelect={(item) => {
							getUsers(item.name);
							setSelectedSystem(item);
							if (selectedUser) {
								setselectedUser(undefined);
							}
						}}
						renderItem={(system) => (
							<Flex flexDirection={"column"}>
								<Heading as="h6" fontSize={ThemeFontSizeVariable.Large}>
									{system.commonName}
								</Heading>
								<div>{system.name}</div>
							</Flex>
						)}
						displayInput="commonName"
						searchProps={["commonName", "name"]}
						placeholder={t("system")}
						isLoading={systemLoading}
					/>
					<SearchInput
						items={availableUsers ?? []}
						selectedItem={selectedUser}
						onSelect={(item) => setselectedUser(item)}
						renderItem={(user) => (
							<Heading as="h6" fontSize={ThemeFontSizeVariable.Large}>
								{user.name}
							</Heading>
						)}
						disabledItem={(user) => user.disableImpersonate}
						displayInput="name"
						searchProps={["name"]}
						placeholder={t("user")}
						isDisabled={!selectedSystem}
						isLoading={userLoading}
					/>
					<Button
						w={"100%"}
						variant={"primary"}
						onClick={() => impersonateUser(selectedUser!.subject)}
						isDisabled={!selectedUser}
					>
						{t("impersonate")}
					</Button>
				</>
			)}
		</Stack>
	);
};

export default ImpersonateSelect;
